import {useCallback, useEffect, useRef, useState} from 'react';

import {assert} from 'Asserts';
import {startWebRTCRealtimeVoiceSession} from 'llm/data-capture/DataCaptureDAO';

import WebRTCSession from './WebRTCSession';

export enum RealtimeVoiceSessionState {
  NOT_CONNECTED = 'NOT_CONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
}

export interface RealtimeVoiceSessionControls {
  sessionState: RealtimeVoiceSessionState;
  startVoiceSession: () => Promise<void>;
  stopVoiceSession: () => void;
}

/**
 * Hook to manage a realtime voice session.
 *
 * Currently this is implemented as a WebRTC connection to OpenAI's Realtime API.
 */
export default function useRealtimeVoiceSession(chatSessionId: string | null): RealtimeVoiceSessionControls {
  const [sessionState, setSessionState] = useState<RealtimeVoiceSessionState>(
    RealtimeVoiceSessionState.NOT_CONNECTED
  );
  const webRTCSessionRef = useRef<WebRTCSession | null>(null);

  // On unmount, always disconnect the session, release the microphone, etc.
  useEffect(
    () => () => {
      webRTCSessionRef.current?.close();
    },
    []
  );

  const stopVoiceSession = useCallback(() => {
    webRTCSessionRef.current?.close();
    webRTCSessionRef.current = null;
    setSessionState(RealtimeVoiceSessionState.NOT_CONNECTED);
  }, []);

  const startVoiceSession = useCallback(async () => {
    assert(chatSessionId != null, 'Unexpectedly started voice session without a chat session ID.');
    setSessionState(RealtimeVoiceSessionState.CONNECTING);

    try {
      const webRTCSession = await WebRTCSession.create();
      webRTCSessionRef.current = webRTCSession;

      const initialSDP = await webRTCSession.createSDPOffer();
      const {sdpAnswer} = await startWebRTCRealtimeVoiceSession(chatSessionId, initialSDP);

      await webRTCSession.startSessionWithSDPAnswer(sdpAnswer);
      setSessionState(RealtimeVoiceSessionState.CONNECTED);
    } catch (error) {
      stopVoiceSession();
      throw error;
    }
  }, [chatSessionId, stopVoiceSession]);

  return {sessionState, startVoiceSession, stopVoiceSession};
}
